import React, { memo, useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react';
import SmartText from '../SmartText';
import { cn } from '../../lib/utils';

/**
 * Click-to-edit text that renders `@[file]` references when idle.
 *
 * Used for task titles and notes in the chat side panel. Enter commits (unless
 * `multiline`, where Shift is inverted), Escape reverts, blur commits. The
 * textarea auto-grows so notes never open a scrollbar inside a 440px panel.
 */
function InlineEditableImpl({
    value,
    onCommit,
    placeholder = 'Empty',
    multiline = false,
    disabled = false,
    className,
    inputClassName,
    emptyClassName,
    testId,
    ariaLabel,
}) {
    const [isEditing, setIsEditing] = useState(false);
    const [draft, setDraft] = useState(value ?? '');
    const inputRef = useRef(null);

    // Re-sync when the task changes underneath us (e.g. the agent renamed it or
    // a revert landed) but never while the user is mid-edit.
    useEffect(() => {
        if (!isEditing) setDraft(value ?? '');
    }, [value, isEditing]);

    const autoGrow = useCallback(() => {
        const el = inputRef.current;
        if (!el || !multiline) return;
        el.style.height = 'auto';
        el.style.height = `${el.scrollHeight}px`;
    }, [multiline]);

    // Focus and drop the caret at the end when entering edit mode. Reads the
    // length off the DOM node so the draft isn't a dependency — otherwise this
    // would re-run (and re-select) on every keystroke.
    useLayoutEffect(() => {
        if (!isEditing) return;
        const el = inputRef.current;
        if (!el) return;
        autoGrow();
        el.focus();
        el.setSelectionRange(el.value.length, el.value.length);
    }, [isEditing, autoGrow]);

    const commit = useCallback(() => {
        setIsEditing(false);
        const next = draft.trim();
        if (next !== (value ?? '').trim()) onCommit(next);
    }, [draft, value, onCommit]);

    const cancel = useCallback(() => {
        setDraft(value ?? '');
        setIsEditing(false);
    }, [value]);

    const handleKeyDown = useCallback((e) => {
        if (e.key === 'Escape') {
            e.preventDefault();
            cancel();
            return;
        }
        if (e.key === 'Enter' && (!multiline || (multiline && (e.metaKey || e.ctrlKey)))) {
            e.preventDefault();
            commit();
        }
    }, [cancel, commit, multiline]);

    if (isEditing) {
        const Tag = multiline ? 'textarea' : 'input';
        return (
            <Tag
                ref={inputRef}
                value={draft}
                rows={multiline ? 1 : undefined}
                aria-label={ariaLabel}
                data-testid={testId ? `${testId}-input` : undefined}
                onChange={(e) => { setDraft(e.target.value); autoGrow(); }}
                onKeyDown={handleKeyDown}
                onBlur={commit}
                onPointerDown={(e) => e.stopPropagation()}
                placeholder={placeholder}
                className={cn(
                    'w-full resize-none rounded-md border border-primary/50 bg-black/40 px-2 py-1',
                    'text-foreground outline-none placeholder:text-muted-foreground/50',
                    'focus-visible:ring-1 focus-visible:ring-ring',
                    multiline ? 'min-h-[2.25rem] leading-relaxed' : 'leading-tight',
                    inputClassName ?? className,
                )}
            />
        );
    }

    const isEmpty = !value || !String(value).trim();

    // A div rather than a button: SmartText renders `@[file]` references as their
    // own clickable chips, and a button cannot contain a button.
    return (
        <div
            tabIndex={disabled ? undefined : 0}
            data-testid={testId}
            aria-label={ariaLabel}
            title={disabled ? undefined : 'Click to edit'}
            onPointerDown={(e) => e.stopPropagation()}
            onClick={(e) => { e.stopPropagation(); if (!disabled) setIsEditing(true); }}
            onKeyDown={(e) => {
                if (disabled) return;
                if (e.key === 'Enter' || e.key === ' ') {
                    e.preventDefault();
                    setIsEditing(true);
                }
            }}
            className={cn(
                'block w-full break-words rounded-md px-2 py-1 text-left transition-colors',
                'focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring',
                disabled ? 'cursor-default' : 'cursor-text hover:bg-white/[0.04]',
                isEmpty && (emptyClassName ?? 'italic text-muted-foreground/50'),
                className,
            )}
        >
            {isEmpty ? placeholder : <SmartText text={String(value)} className="inline" />}
        </div>
    );
}

export const InlineEditable = memo(InlineEditableImpl);
export default InlineEditable;
